import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Stack,
  alpha,
  useTheme,
  Chip,
  Tooltip,
  Button,
  Avatar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { useTranslation } from "react-i18next";
import { getInitials } from "../../../utils/utils";

const MAX_VISIBLE_MEMBERS = 4;

function BoardMemberStrip({ members = [], onClick }) {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const visible = members.slice(0, MAX_VISIBLE_MEMBERS);
  const overflow = members.length - visible.length;

  if (!members.length) {
    return (
      <Tooltip title={t("boardMembersMenu")}>
        <IconButton size="small" onClick={onClick} sx={{ ml: 0.5 }}>
          <GroupOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={t("boardMembersMenu")}>
      <Button
        size="small"
        onClick={onClick}
        sx={{
          minWidth: 0,
          px: 0.75,
          py: 0.25,
          borderRadius: 999,
          color: "text.primary",
          bgcolor: alpha(theme.palette.action.active, 0.06),
          border: `1px solid ${alpha(theme.palette.divider, 0.35)}`,
          transition: "background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease",
          "&:hover": {
            bgcolor: alpha(theme.palette.action.active, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.25),
            transform: "translateY(-1px)",
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={-0.75}>
          {visible.map((member, index) => (
            <Avatar
              key={member.id}
              src={member.userAvatarUrl || undefined}
              alt=""
              sx={{
                width: 28,
                height: 28,
                fontSize: "0.65rem",
                fontWeight: 600,
                border: `2px solid ${theme.palette.background.paper}`,
                ml: index > 0 ? -0.75 : 0,
                zIndex: visible.length - index,
                bgcolor: "primary.main",
              }}
            >
              {getInitials(member.userName || member.participantId)}
            </Avatar>
          ))}
          {overflow > 0 ? (
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: "0.62rem",
                fontWeight: 600,
                ml: -0.75,
                bgcolor: alpha(theme.palette.text.primary, 0.1),
                color: "text.secondary",
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            >
              +{overflow}
            </Avatar>
          ) : null}
        </Stack>
      </Button>
    </Tooltip>
  );
}

const BoardHeader = ({
  board,
  members = [],
  onMembersClick,
  onBack,
  compact = false,
  standalone = false,
  canManage = false,
  organizingColumns = false,
  onToggleOrganizeColumns,
  onEdit,
  onDelete,
  onOpenAnalytics,
  onOpenPopup,
  flushBottom = false,
}) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const isWorkspaceBoard = !board?.spaceId && !board?.leadId;
  const cardCount = (board?.columns || []).reduce(
    (sum, col) => sum + (col.cards?.length || 0),
    0
  );

  const handleCloseWindow = () => {
    window.close();
  };

  return (
    <Box
      sx={{
        px: compact ? 1.5 : 2,
        py: compact ? 0.75 : 1.25,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.78 : 0.9),
        backdropFilter: "blur(14px)",
        borderBottom: flushBottom
          ? "none"
          : `1px solid ${alpha(theme.palette.divider, 0.45)}`,
        boxShadow: flushBottom
          ? "none"
          : (th) =>
              th.palette.mode === "dark"
                ? `0 4px 20px ${alpha(th.palette.common.black, 0.25)}`
                : `0 1px 0 ${alpha(th.palette.divider, 0.6)}, 0 4px 16px ${alpha(th.palette.common.black, 0.04)}`,
        position: "relative",
        zIndex: 2,
      }}
    >
      {((!compact && !standalone) || (standalone && onBack)) && (
        <Tooltip title={t("backToBoards")}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {standalone && (
        <Tooltip title={t("closeWindow")}>
          <IconButton onClick={handleCloseWindow} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography
            variant={compact ? "subtitle2" : "subtitle1"}
            fontWeight={700}
            noWrap
            sx={{ maxWidth: { xs: "100%", sm: 480 }, letterSpacing: -0.15 }}
          >
            {board?.name}
          </Typography>
          {cardCount > 0 && (
            <Chip
              size="small"
              label={t("cardCount", { count: cardCount })}
              sx={{
                height: 22,
                fontSize: "0.68rem",
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: "primary.main",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            />
          )}
        </Stack>
        {!compact && board?.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.25,
              maxWidth: 640,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {board.description}
          </Typography>
        )}
      </Box>

      {onMembersClick && !organizingColumns ? (
        <BoardMemberStrip members={members} onClick={onMembersClick} />
      ) : null}

      {organizingColumns && canManage && onToggleOrganizeColumns ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ display: { xs: "none", md: "block" }, whiteSpace: "nowrap" }}
          >
            {t("organizeColumnsActiveHint")}
          </Typography>
          <Tooltip title={t("organizeColumnsDoneHint")}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckOutlinedIcon />}
              onClick={onToggleOrganizeColumns}
              sx={{
                fontWeight: 800,
                borderRadius: 2,
                whiteSpace: "nowrap",
                px: { xs: 1.25, sm: 1.75 },
                boxShadow: 2,
              }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                {t("organizeColumnsDone")}
              </Box>
              <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                {t("organizeColumnsDoneShort")}
              </Box>
            </Button>
          </Tooltip>
        </Stack>
      ) : null}

      {!standalone && onOpenPopup && !organizingColumns ? (
        <Tooltip title={t("openInPopup")}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewOutlinedIcon />}
            onClick={onOpenPopup}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              fontWeight: 600,
              borderRadius: 2,
              flexShrink: 0,
              borderColor: alpha(theme.palette.divider, 0.6),
              transition: "border-color 0.15s ease, background-color 0.15s ease",
              "&:hover": {
                borderColor: "primary.light",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            {t("openInPopup")}
          </Button>
        </Tooltip>
      ) : null}

      {!standalone && onOpenPopup && !organizingColumns ? (
        <Tooltip title={t("openInPopup")}>
          <IconButton
            onClick={onOpenPopup}
            size="small"
            sx={{ display: { xs: "inline-flex", sm: "none" } }}
          >
            <OpenInNewOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}

      {(canManage || onOpenAnalytics || onMembersClick) && (
        <>
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              bgcolor: alpha(theme.palette.action.active, 0.06),
              "&:hover": { bgcolor: alpha(theme.palette.action.active, 0.12) },
            }}
          >
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{ sx: { minWidth: 260 } }}
          >
            {onMembersClick ? (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onMembersClick();
                }}
              >
                <ListItemIcon>
                  <GroupOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("boardMembersMenu")}
                  secondary={t("boardMembersMenuHint")}
                />
              </MenuItem>
            ) : null}
            {onOpenAnalytics ? (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onOpenAnalytics();
                }}
              >
                <ListItemIcon>
                  <InsightsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("boardAnalytics")}
                  secondary={t("boardAnalyticsMenuHint")}
                />
              </MenuItem>
            ) : null}
            {(onMembersClick || onOpenAnalytics) && canManage ? <Divider /> : null}
            {canManage && onToggleOrganizeColumns && !organizingColumns ? (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onToggleOrganizeColumns();
                }}
              >
                <ListItemIcon>
                  <ViewColumnOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t("organizeColumns")}
                  secondary={t("organizeColumnsMenuHint")}
                />
              </MenuItem>
            ) : null}
            {canManage && onToggleOrganizeColumns && !organizingColumns ? <Divider /> : null}
            {canManage ? (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onEdit?.();
              }}
            >
              <ListItemIcon>
                <EditOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("editBoard")} secondary={t("editBoardMenuHint")} />
            </MenuItem>
            ) : null}
            {canManage && isWorkspaceBoard && (
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  onDelete?.();
                }}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText primary={t("deleteBoard")} secondary={t("deleteBoardMenuHint")} />
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default BoardHeader;
